import React from "react";
import { Link } from "react-router-dom";

/**
 * Cluster article — "Cómo empezar a moverte con sobrepeso".
 * Category: activation. Interlinks back to the pillar with rich keyword anchors.
 */

function Section({ title, children, id }) {
  return (
    <section className="space-y-4" id={id}>
      <h2 className="font-sans-display text-2xl sm:text-3xl text-[#0B2545] tracking-tight leading-snug mt-12 mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Pullquote({ children }) {
  return (
    <blockquote className="border-l-2 border-[#B4C5A4] pl-5 py-1 my-6 font-sans-display text-lg sm:text-xl text-[#0B2545] tracking-tight leading-snug italic">
      {children}
    </blockquote>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//                                CATALÀ
// ════════════════════════════════════════════════════════════════════════════
export function ComencarSobrepesCA() {
  return (
    <>
      <p>
        {"Fa anys que no et mous i saps que t'hauries de moure. Ho saps tu i t'ho han dit deu vegades. Però cada vegada que has intentat començar, o t'has fet mal, o t'has quedat sense aire al minut, o has arribat a casa sentint-te pitjor que abans. La conclusió és sempre la mateixa: aquest cos no està preparat per fer el que fan els altres."}
      </p>
      <p>
        {"I tens raó a mitges. El teu cos no està preparat per fer el que fan els altres. Però sí que està preparat per fer el que és teu. Aquest article no és una llista d'exercicis genèrics — és una manera realista de tornar a moure't quan tens sobrepès, sense forçar-te i sense muntar-te expectatives que et tornaran a fer mal."}
      </p>

      <Section title="Per què els mètodes generals no funcionen amb tu" id="metodes-generals">
        <p>
          {"La majoria de plans d'exercici estan pensats per a algú que ja fa esport o que fa poc que l'ha deixat. Comencen assumint que pots caminar 30 minuts, que pots aixecar-te del terra sense ajuda, que no et fan mal els genolls, que el cor et respon com un cor de 25 anys. Si el teu cos no compleix aquestes premisses, el pla comença suposant una cosa que tu no ets. I quan falles, sembla que és culpa teva. No ho és."}
        </p>
        <p>
          {"El primer canvi de mentalitat és aquest: no comences per on comença un pla; comences per on comença el teu cos. Això vol dir sovint fer coses que semblen ridícules a algú que ja s'ha activat. Ridícules per a ells, essencials per a tu."}
        </p>
        <Pullquote>
          {"No fallis un mètode. Deixa un mètode que no està fet per a tu."}
        </Pullquote>
      </Section>

      <Section title="El primer pas real: caminar (però no com creus)" id="caminar">
        <p>
          {"Caminar és l'única activitat que gairebé qualsevol cos pot començar a fer avui. Però hi ha una trampa: la majoria de gent que comença dient 'sortiré a caminar' fa 30 minuts el primer dia, es cansa, l'endemà li fan mal les cames, ho deixa al tercer dia. La intensitat no ha de ser 30 minuts. Ha de ser el que et deixi tornar demà."}
        </p>
        <p>
          {"Comença amb 5 minuts. Cinc. Al carrer de casa, a pas normal, sense àudio, sense contar passos, sense mirar el mòbil. L'objectiu no és cremar calories — és tornar a associar el moviment amb 'això no em fa mal'. Cinc minuts durant una setmana. Si un dia et sents malament, tres minuts. Si estàs cansat, aturar-te i mirar per la finestra també val."}
        </p>
        <p>
          {"Quan cinc minuts se't fa curt, puges a set. Quan set se't fa curt, deu. Sempre pujant menys del que et diu el cap, més del que et diu el cos."}
        </p>
      </Section>

      <Section title="Què fer si et fan mal els genolls o l'esquena" id="dolor">
        <p>
          {"Si comences a moure't i et fa mal, no vol dir que estiguis fet. Vol dir que hi ha una part del cos que ha estat compensant durant anys. Els genolls fan mal moltes vegades perquè la musculatura al voltant (glutis, quàdriceps, isquiotibials) no aguanta el pes. L'esquena fa mal perquè el core i les cames no col·laboren."}
        </p>
        <p>
          {"La primera cosa útil no és 'caminar més fluix' — és fer una avaluació honesta. Si el dolor és intens (7 sobre 10 o més), es dispara a altres zones o t'impedeix dormir, para i consulta un fisioterapeuta o el teu metge. Si és molèstia (3-4 sobre 10) i desapareix quan pares, pots començar amb moviments més curts i afegir treball de força específic per la zona afectada. Aquí no s'improvisa: cada dolor té la seva causa."}
        </p>
        <Pullquote>
          {"El dolor no és el teu enemic. És un missatge. Cal escoltar-lo, no ignorar-lo ni tenir-hi por."}
        </Pullquote>
      </Section>

      <Section title="La força és més important que la cardio (quan comences)" id="forca">
        <p>
          {"Aquest és el consell que gairebé ningú et donarà però que canvia tot: si comences amb sobrepès i fa anys que no et mous, treballar força és més important que fer cardio. Motiu simple: la força és el que et permet fer les activitats quotidianes sense esgotar-te. Pujar escales, aixecar-te d'una cadira, portar la compra."}
        </p>
        <p>
          {"No cal gimnàs. No cal peses. Comença amb tres moviments senzills, tres cops per setmana, cinc repeticions cada un:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"Aixecar-te i tornar-te a asseure a una cadira (sit-to-stand). Si costa, usa mans a les cuixes. Cinc vegades."}</li>
          <li>{"Empènyer una paret amb els braços com si fossis flexions verticals. Cinc vegades."}</li>
          <li>{"Aixecar els talons estant dret, agafant-te a una taula o a la paret. Cinc vegades."}</li>
        </ul>
        <p>
          {"Tres exercicis, cinc repeticions, dos minuts en total. Si això t'esgota, és exactament el punt on cal començar. Si et sembla molt fàcil, pots pujar a 8 repeticions la setmana següent — mai més ràpid."}
        </p>
      </Section>

      <Section title="Menjar: canvia una cosa, no deu" id="menjar">
        <p>
          {"No parlarem de dieta perquè aquest article no és sobre perdre pes ràpid. Però hi ha un principi que ajuda quan comences a moure't: el cos té més gasolina si menges regular. No vol dir menjar més, vol dir menjar en horaris estables. Molta gent amb sobrepès menja poc al matí i molt a la nit — el cos entra en mode 'no sé quan vindrà el proper menjar' i estalvia energia."}
        </p>
        <p>
          {"Un sol canvi durant les primeres setmanes: esmorzar cada dia. No cal que sigui molt — un iogurt amb fruita, un ou dur, una torrada. La idea és entrenar el cos a saber que menjarà de manera previsible. Amb això, el moviment comença a demanar menys esforç mental."}
        </p>
      </Section>

      <Section title="Com saber si estàs progressant (sense pesar-te)" id="progres">
        <p>
          {"Si l'única mesura del teu progrés és la bàscula, el teu cervell trigarà mesos a veure resultats i abandonaràs. Hi ha altres senyals que apareixen molt abans i que compten més:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"Puges les escales i respires una mica millor."}</li>
          <li>{"T'aixeques del sofà sense empènyer amb les mans."}</li>
          <li>{"Dorms millor (o pitjor un dia, però amb un patró que reconeixes)."}</li>
          <li>{"La roba et queda igual però et sents diferent a dins."}</li>
          <li>{"Fas 5 minuts extra caminant sense pensar-hi."}</li>
        </ul>
        <p>
          {"Anota-ho. Un simple full o una nota al mòbil cada diumenge: com he estat aquesta setmana? Què he pogut fer que fa un mes no podia? Aquest registre és la millor eina emocional quan tinguis un dia dolent — perquè els tindràs."}
        </p>
        <Pullquote>
          {"El progrés real gairebé mai és lineal. El que canvia és la teva relació amb el teu cos, no els kg de la bàscula."}
        </Pullquote>
      </Section>

      <Section title="I si tornes a fallar?" id="fallar">
        <p>
          {"Passarà. Un dia et saltaràs els 5 minuts. Una setmana no faràs res. Un mes pensaràs que ja no ho recuperaràs. És normal. El que fa la diferència no és no fallar — és tornar. Sense drama, sense càstig, sense 'ara compensaré'. Simplement tornes a fer els cinc minuts. Un dia. Demà, un altre."}
        </p>
        <p>
          {"Si aquest patró d'anar i tornar et sona familiar, hem escrit un article específic sobre "}
          <Link to="/blog/per-que-abandono-sempre-el-canvi" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            per què sempre abandones — i com trencar el patró aquesta vegada
          </Link>
          {". Explica els 3 moments crítics on gairebé tothom abandona i com preparar-se per superar-los sense caure a la trampa del 'tot o res'."}
        </p>
        <p>
          {"I si part del que et frena és sentir vergonya de moure't davant dels altres, també parlem específicament de "}
          <Link to="/blog/em-fa-vergonya-anar-al-gimnas" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            per què et fa vergonya anar al gimnàs
          </Link>
          {" i quins espais funcionen millor quan comences."}
        </p>
        <p>
          {"Si vols entendre millor per què tornar a començar és el 90% de la feina (i on rau el veritable canvi), pots llegir la nostra guia central sobre "}
          <Link to="/blog/com-activar-te-quan-fa-anys-que-no-et-mous" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            com activar-te quan fa anys que no et mous
          </Link>
          {". Allà aprofundim en la trampa del dilluns, la memòria emocional del cos i com construir una identitat de persona que es mou — sense grans propòsits."}
        </p>
        <p>
          {"Aquest article és només un mapa inicial. Si vols fer un pas més cap a entendre el teu punt de partida concret, deixa'ns el teu correu al formulari de baix i et farem arribar el test inicial amb preguntes específiques per al teu cas."}
        </p>
      </Section>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//                                CASTELLANO
// ════════════════════════════════════════════════════════════════════════════
export function ComenzarSobrepesoES() {
  return (
    <>
      <p>
        {"Llevas años sin moverte y sabes que deberías moverte. Lo sabes tú y te lo han dicho diez veces. Pero cada vez que has intentado empezar, o te has hecho daño, o te has quedado sin aire al minuto, o has llegado a casa sintiéndote peor que antes. La conclusión es siempre la misma: este cuerpo no está preparado para hacer lo que hacen los demás."}
      </p>
      <p>
        {"Y tienes razón a medias. Tu cuerpo no está preparado para hacer lo que hacen los demás. Pero sí está preparado para hacer lo que es tuyo. Este artículo no es una lista de ejercicios genéricos — es una manera realista de volver a moverte cuando tienes sobrepeso, sin forzarte y sin montarte expectativas que te volverán a hacer daño."}
      </p>

      <Section title="Por qué los métodos generales no funcionan contigo" id="metodos-generales">
        <p>
          {"La mayoría de planes de ejercicio están pensados para alguien que ya hace deporte o que lo dejó hace poco. Empiezan asumiendo que puedes caminar 30 minutos, que puedes levantarte del suelo sin ayuda, que no te duelen las rodillas, que el corazón te responde como un corazón de 25 años. Si tu cuerpo no cumple estas premisas, el plan empieza suponiendo una cosa que tú no eres. Y cuando fallas, parece que es culpa tuya. No lo es."}
        </p>
        <p>
          {"El primer cambio de mentalidad es este: no empiezas por donde empieza un plan; empiezas por donde empieza tu cuerpo. Eso significa a menudo hacer cosas que parecen ridículas a alguien que ya se ha activado. Ridículas para ellos, esenciales para ti."}
        </p>
        <Pullquote>
          {"No falles un método. Dejas un método que no está hecho para ti."}
        </Pullquote>
      </Section>

      <Section title="El primer paso real: caminar (pero no como crees)" id="caminar">
        <p>
          {"Caminar es la única actividad que casi cualquier cuerpo puede empezar a hacer hoy. Pero hay una trampa: la mayoría de gente que empieza diciendo 'saldré a caminar' hace 30 minutos el primer día, se cansa, al día siguiente le duelen las piernas, lo deja al tercer día. La intensidad no debe ser 30 minutos. Debe ser lo que te permita volver mañana."}
        </p>
        <p>
          {"Empieza con 5 minutos. Cinco. En la calle de casa, a paso normal, sin audio, sin contar pasos, sin mirar el móvil. El objetivo no es quemar calorías — es volver a asociar el movimiento con 'esto no me hace daño'. Cinco minutos durante una semana. Si un día te sientes mal, tres minutos. Si estás cansado, pararte y mirar por la ventana también vale."}
        </p>
        <p>
          {"Cuando cinco minutos se te haga corto, subes a siete. Cuando siete se te haga corto, diez. Siempre subiendo menos de lo que te dice la cabeza, más de lo que te dice el cuerpo."}
        </p>
      </Section>

      <Section title="Qué hacer si te duelen las rodillas o la espalda" id="dolor">
        <p>
          {"Si empiezas a moverte y te duele, no significa que estés acabado. Significa que hay una parte del cuerpo que ha estado compensando durante años. Las rodillas duelen muchas veces porque la musculatura alrededor (glúteos, cuádriceps, isquiotibiales) no aguanta el peso. La espalda duele porque el core y las piernas no colaboran."}
        </p>
        <p>
          {"Lo primero útil no es 'caminar más suave' — es hacer una evaluación honesta. Si el dolor es intenso (7 sobre 10 o más), se dispara a otras zonas o te impide dormir, para y consulta a un fisioterapeuta o a tu médico. Si es molestia (3-4 sobre 10) y desaparece cuando paras, puedes empezar con movimientos más cortos y añadir trabajo de fuerza específico para la zona afectada. Aquí no se improvisa: cada dolor tiene su causa."}
        </p>
        <Pullquote>
          {"El dolor no es tu enemigo. Es un mensaje. Hay que escucharlo, no ignorarlo ni tenerle miedo."}
        </Pullquote>
      </Section>

      <Section title="La fuerza es más importante que el cardio (cuando empiezas)" id="fuerza">
        <p>
          {"Este es el consejo que casi nadie te dará pero que lo cambia todo: si empiezas con sobrepeso y llevas años sin moverte, trabajar fuerza es más importante que hacer cardio. Motivo simple: la fuerza es lo que te permite hacer las actividades cotidianas sin agotarte. Subir escaleras, levantarte de una silla, cargar la compra."}
        </p>
        <p>
          {"No hace falta gimnasio. No hacen falta pesas. Empieza con tres movimientos sencillos, tres veces por semana, cinco repeticiones cada uno:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"Levantarte y volver a sentarte en una silla (sit-to-stand). Si cuesta, apoya manos en muslos. Cinco veces."}</li>
          <li>{"Empujar una pared con los brazos como si fueran flexiones verticales. Cinco veces."}</li>
          <li>{"Levantar los talones estando de pie, agarrándote a una mesa o pared. Cinco veces."}</li>
        </ul>
        <p>
          {"Tres ejercicios, cinco repeticiones, dos minutos en total. Si eso te agota, es exactamente el punto donde hay que empezar. Si te parece muy fácil, puedes subir a 8 repeticiones la semana siguiente — nunca más rápido."}
        </p>
      </Section>

      <Section title="Comer: cambia una cosa, no diez" id="comer">
        <p>
          {"No hablaremos de dieta porque este artículo no es sobre perder peso rápido. Pero hay un principio que ayuda cuando empiezas a moverte: el cuerpo tiene más gasolina si comes regular. No significa comer más, significa comer en horarios estables. Mucha gente con sobrepeso come poco por la mañana y mucho por la noche — el cuerpo entra en modo 'no sé cuándo vendrá la próxima comida' y ahorra energía."}
        </p>
        <p>
          {"Un solo cambio durante las primeras semanas: desayunar cada día. No hace falta que sea mucho — un yogur con fruta, un huevo duro, una tostada. La idea es entrenar al cuerpo a saber que comerá de manera previsible. Con eso, el movimiento empieza a exigir menos esfuerzo mental."}
        </p>
      </Section>

      <Section title="Cómo saber si estás progresando (sin pesarte)" id="progreso">
        <p>
          {"Si la única medida de tu progreso es la báscula, tu cerebro tardará meses en ver resultados y abandonarás. Hay otras señales que aparecen mucho antes y que cuentan más:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"Subes las escaleras y respiras un poco mejor."}</li>
          <li>{"Te levantas del sofá sin empujarte con las manos."}</li>
          <li>{"Duermes mejor (o peor un día, pero con un patrón que reconoces)."}</li>
          <li>{"La ropa te queda igual pero te sientes diferente por dentro."}</li>
          <li>{"Haces 5 minutos extra caminando sin pensarlo."}</li>
        </ul>
        <p>
          {"Anótalo. Una simple hoja o una nota en el móvil cada domingo: ¿cómo he estado esta semana? ¿Qué he podido hacer que hace un mes no podía? Ese registro es la mejor herramienta emocional cuando tengas un día malo — porque los tendrás."}
        </p>
        <Pullquote>
          {"El progreso real casi nunca es lineal. Lo que cambia es tu relación con tu cuerpo, no los kg de la báscula."}
        </Pullquote>
      </Section>

      <Section title="¿Y si vuelves a fallar?" id="fallar">
        <p>
          {"Pasará. Un día te saltarás los 5 minutos. Una semana no harás nada. Un mes pensarás que ya no lo recuperarás. Es normal. Lo que marca la diferencia no es no fallar — es volver. Sin drama, sin castigo, sin 'ahora compensaré'. Simplemente vuelves a hacer los cinco minutos. Un día. Mañana, otro."}
        </p>
        <p>
          {"Si este patrón de ir y volver te suena familiar, hemos escrito un artículo específico sobre "}
          <Link to="/es/blog/por-que-abandono-siempre-el-cambio" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            por qué siempre abandonas — y cómo romper el patrón esta vez
          </Link>
          {". Explica los 3 momentos críticos donde casi todo el mundo abandona y cómo prepararte para superarlos sin caer en la trampa del 'todo o nada'."}
        </p>
        <p>
          {"Y si parte de lo que te frena es sentir vergüenza de moverte delante de los demás, también hablamos específicamente de "}
          <Link to="/es/blog/me-da-verguenza-ir-al-gimnasio" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            por qué te da vergüenza ir al gimnasio
          </Link>
          {" y qué espacios funcionan mejor cuando empiezas."}
        </p>
        <p>
          {"Si quieres entender mejor por qué volver a empezar es el 90% del trabajo (y donde reside el verdadero cambio), puedes leer nuestra guía central sobre "}
          <Link to="/es/blog/como-activarte-cuando-llevas-anos-sin-moverte" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            cómo activarte cuando llevas años sin moverte
          </Link>
          {". Allí profundizamos en la trampa del lunes, la memoria emocional del cuerpo y cómo construir una identidad de persona que se mueve — sin grandes propósitos."}
        </p>
        <p>
          {"Este artículo es solo un mapa inicial. Si quieres dar un paso más para entender tu punto de partida concreto, déjanos tu correo en el formulario de abajo y te haremos llegar el test inicial con preguntas específicas para tu caso."}
        </p>
      </Section>
    </>
  );
}
